/* eslint-disable react/prop-types */
import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Button,
  Box,
  Typography,
  LinearProgress,
  Paper,
  Alert,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

import CloseIcon from "@mui/icons-material/Close";
import SecurityIcon from "@mui/icons-material/Security";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import useEditData from "@/hooks/editHooks/useEditData";
import { useTranslation } from "react-i18next";

const ChangePassword = ({ onClose, setToast }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Initialize mutation for changing password
  const { mutateAsync: editMutation, isPending: isLoading } = useEditData(
    "/api/v1/users/password",
    ["user", "password"]
  );

  const getPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[a-z]/.test(password)) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 12.5;
    if (/[^A-Za-z0-9]/.test(password)) strength += 12.5;
    return Math.min(strength, 100);
  };

  const getStrengthColor = (strength) => {
    if (strength < 30) return "error";
    if (strength < 60) return "warning";
    if (strength < 80) return "info";
    return "success";
  };

  const getStrengthLabel = (strength) => {
    if (strength < 30) return t("changePassword.strengthWeak");
    if (strength < 60) return t("changePassword.strengthFair");
    if (strength < 80) return t("changePassword.strengthGood");
    return t("changePassword.strengthStrong");
  };

  const validatePassword = (password) => {
    if (!password.trim()) {
      return t("changePassword.newPasswordRequiredError");
    }

    // Comprehensive password validation
    const validationErrors = [];

    if (password.length < 8) {
      validationErrors.push(t("changePassword.newPasswordLengthError"));
    }

    if (!/[a-z]/.test(password)) {
      validationErrors.push(t("changePassword.validationLowercaseError"));
    }

    if (!/[A-Z]/.test(password)) {
      validationErrors.push(t("changePassword.validationUppercaseError"));
    }

    if (!/[0-9]/.test(password)) {
      validationErrors.push(t("changePassword.validationNumberError"));
    }

    if (!/[^A-Za-z0-9]/.test(password)) {
      validationErrors.push(t("changePassword.validationSpecialCharError"));
    }

    if (validationErrors.length > 0) {
      // Show all specific errors or a general message if too many
      if (validationErrors.length === 1) {
        return validationErrors[0];
      } else {
        return t("changePassword.validationAllRequirementsError");
      }
    }

    return ""; // No error
  };

  const handlePasswordBlur = () => {
    const passwordError = validatePassword(newPassword);
    setErrors((prev) => ({
      ...prev,
      newPassword: passwordError,
    }));
  };

  const handleConfirmPasswordBlur = () => {
    if (!confirmPassword.trim()) {
      setErrors((prev) => ({
        ...prev,
        confirmPassword: t("changePassword.confirmPasswordRequiredError"),
      }));
    } else if (newPassword !== confirmPassword) {
      setErrors((prev) => ({
        ...prev,
        confirmPassword: t("changePassword.confirmPasswordMatchError"),
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        confirmPassword: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    };
    let isValid = true;

    if (!oldPassword.trim()) {
      newErrors.oldPassword = t("changePassword.oldPasswordRequiredError");
      isValid = false;
    }

    // Use the same password validation logic
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      newErrors.newPassword = passwordError;
      isValid = false;
    }

    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = t(
        "changePassword.confirmPasswordRequiredError"
      );
      isValid = false;
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = t("changePassword.confirmPasswordMatchError");
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const sentData = {
      current_password: oldPassword,
      new_password: newPassword,
    };

    try {
      await editMutation(sentData, {
        onSuccess: () => {
          setToast({
            open: true,
            severity: "success",
            message: t("changePassword.successToast"),
          });
          setOldPassword("");
          setNewPassword("");
          setConfirmPassword("");
          onClose();
        },
        onError: (err) => {
          setToast({
            open: true,
            severity: "error",
            message: t("changePassword.errorToast", { error: err.message }),
          });
        },
      });
    } catch (err) {
      setToast({
        open: true,
        severity: "error",
        message: t("changePassword.errorToast", {
          error: err.response.data.message,
        }),
      });
    }
  };

  const passwordStrength = getPasswordStrength(newPassword);
  const strengthColor = getStrengthColor(passwordStrength);
  const strengthLabel = getStrengthLabel(passwordStrength);

  return (
    <Dialog
      open
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 3,
          boxShadow: theme.shadows[24],
          border: "1px solid",
          borderColor: "divider",
          background: theme.palette.background.paper,
          overflow: "hidden",
        },
      }}
      aria-labelledby="change-password-dialog-title"
    >
      <DialogTitle
        id="change-password-dialog-title"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 3,
          px: 4,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <SecurityIcon color="primary" sx={{ fontSize: 28 }} />
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "text.primary",
              }}
            >
              {t("changePassword.title")}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mt: 0.5,
              }}
            >
              {t("changePassword.updatePasswordSubtitle")}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            bgcolor: alpha(theme.palette.text.primary, 0.08),
            "&:hover": {
              bgcolor: alpha(theme.palette.text.primary, 0.12),
              transform: "scale(1.05)",
            },
            transition: "all 0.2s ease",
          }}
          aria-label={t("changePassword.closeDialogAria")}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 4 }}>
        <Box component="form" onSubmit={handleSubmit} sx={{ space: 3 }}>
          {/* Security Notice */}
          <Alert
            severity="info"
            sx={{
              mb: 3,
              borderRadius: 2,
              border: "1px solid",
              borderColor: alpha(theme.palette.info.main, 0.2),
            }}
          >
            <Typography variant="body2">
              {t("changePassword.securityNotice")}
            </Typography>
          </Alert>

          {/* Current Password */}
          <Paper
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              background: alpha(theme.palette.grey[50], 0.5),
              ...(theme.palette.mode === "dark" && {
                background: alpha(theme.palette.grey[800], 0.5),
              }),
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                color: "text.secondary",
                mb: 2,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontWeight: 600,
                fontSize: "0.75rem",
              }}
            >
              {t("changePassword.currentPasswordSection")}
            </Typography>
            <TextField
              fullWidth
              type="password"
              label={t("changePassword.oldPasswordLabel")}
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              required
              error={!!errors.oldPassword}
              helperText={errors.oldPassword}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "background.paper",
                },
              }}
              aria-label={t("changePassword.oldPasswordAria")}
            />
          </Paper>

          {/* New Password Section */}
          <Paper
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              border: "1px solid",
              borderColor: alpha(theme.palette.primary.main, 0.1),
              background: alpha(theme.palette.primary.main, 0.02),
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                color: "text.secondary",
                mb: 2,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontWeight: 600,
                fontSize: "0.75rem",
              }}
            >
              {t("changePassword.newPasswordSection")}
            </Typography>

            <TextField
              fullWidth
              type="password"
              label={t("changePassword.newPasswordLabel")}
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                // Clear error when user starts typing
                if (errors.newPassword) {
                  setErrors((prev) => ({ ...prev, newPassword: "" }));
                }
              }}
              onBlur={handlePasswordBlur}
              required
              error={!!errors.newPassword}
              helperText={errors.newPassword}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "background.paper",
                },
              }}
              aria-label={t("changePassword.newPasswordAria")}
            />

            {/* Password Strength Indicator */}
            {newPassword && (
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {t("changePassword.passwordStrengthLabel")}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {passwordStrength >= 80 ? (
                      <CheckCircleIcon
                        sx={{ fontSize: 16, color: "success.main" }}
                      />
                    ) : (
                      <ErrorIcon
                        sx={{ fontSize: 16, color: `${strengthColor}.main` }}
                      />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        color: `${strengthColor}.main`,
                        fontWeight: 600,
                      }}
                    >
                      {strengthLabel}
                    </Typography>
                  </Box>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={passwordStrength}
                  color={strengthColor}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: alpha(theme.palette.grey[300], 0.3),
                  }}
                />
              </Box>
            )}

            <TextField
              fullWidth
              type="password"
              label={t("changePassword.confirmPasswordLabel")}
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                // Clear error when user starts typing
                if (errors.confirmPassword) {
                  setErrors((prev) => ({ ...prev, confirmPassword: "" }));
                }
              }}
              onBlur={handleConfirmPasswordBlur}
              required
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "background.paper",
                },
              }}
              aria-label={t("changePassword.confirmPasswordAria")}
            />
          </Paper>

          {/* Password Requirements */}
          <Paper
            sx={{
              p: 3,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              background: alpha(theme.palette.warning.main, 0.02),
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                color: "text.secondary",
                mb: 2,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontWeight: 600,
                fontSize: "0.75rem",
              }}
            >
              {t("changePassword.passwordRequirementsSection")}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {[
                {
                  test: newPassword.length >= 8,
                  text: t("changePassword.requirementMinLength"),
                },
                {
                  test: /[a-z]/.test(newPassword),
                  text: t("changePassword.requirementLowercase"),
                },
                {
                  test: /[A-Z]/.test(newPassword),
                  text: t("changePassword.requirementUppercase"),
                },
                {
                  test: /[0-9]/.test(newPassword),
                  text: t("changePassword.requirementNumber"),
                },
                {
                  test: /[^A-Za-z0-9]/.test(newPassword),
                  text: t("changePassword.requirementSpecialChar"),
                },
              ].map((req, index) => (
                <Box
                  key={index}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  {req.test ? (
                    <CheckCircleIcon
                      sx={{ fontSize: 16, color: "success.main" }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        border: "2px solid",
                        borderColor: "text.disabled",
                      }}
                    />
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      color: req.test ? "success.main" : "text.secondary",
                      fontWeight: req.test ? 500 : 400,
                    }}
                  >
                    {req.text}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 4,
          pt: 0,
          gap: 2,
          justifyContent: "flex-end",
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={isLoading}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {t("changePassword.cancelButton")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            isLoading ||
            !oldPassword ||
            !newPassword ||
            !confirmPassword ||
            getPasswordStrength(newPassword) < 100
          }
          sx={{
            borderRadius: 2,
            px: 4,
            py: 1.5,
            textTransform: "none",
            fontWeight: 600,
            boxShadow: theme.shadows[2],
            "&:hover": {
              boxShadow: theme.shadows[4],
            },
            "&.Mui-disabled": {
              bgcolor: alpha(theme.palette.action.disabled, 0.12),
            },
          }}
          aria-label={t("changePassword.saveButtonAria")}
        >
          {isLoading
            ? t("changePassword.savingButton")
            : t("changePassword.saveButton")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePassword;
